import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent, DiscountUpdateEvent, runCartMutation, isLatestCartUpdate } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

function isCartSnapshot(data) {
  return Boolean(data && Number.isSafeInteger(data.total_price)
    && Number.isInteger(data.item_count) && data.item_count >= 0
    && typeof data.currency === 'string' && /^[A-Z]{3}$/.test(data.currency));
}

function isFullCartResponse(data) {
  return isCartSnapshot(data) && Array.isArray(data.discount_codes);
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #sectionsToUpdate() {
    const sectionIds = new Set([this.dataset.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    });
    return [...sectionIds];
  }

  async #reconcileCart(cartUpdateSequence) {
    try {
      const root = window.Shopify?.routes?.root;
      const localeRoot = typeof root === 'string' && root.startsWith('/') ? root : '/';
      const response = await fetch(`${localeRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('The cart could not be refreshed.');
      const cart = await response.json();
      if (!isCartSnapshot(cart)) throw new Error('The refreshed cart was incomplete.');
      if (!isLatestCartUpdate(cartUpdateSequence)) return false;
      document.dispatchEvent(new DiscountUpdateEvent(cart, this.id, cartUpdateSequence));
      return true;
    } catch {
      if (isLatestCartUpdate(cartUpdateSequence)) {
        document.dispatchEvent(new CartUpdateEvent({}, this.id, {
          source: 'cart-discount',
          didError: true,
          cartReadFailed: true,
          addOutcome: 'UNKNOWN',
          cartUpdateSequence,
        }));
      }
      return false;
    }
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    return runCartMutation(async (cartUpdateSequence) => {
      let cartUpdateDispatched = false;
      let discountResponseConfirmed = false;
      try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: this.#sectionsToUpdate(),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Shopify did not confirm the discount update.');
      const data = await response.json();
      if (!isFullCartResponse(data)) throw new Error('Shopify returned an incomplete cart after the discount update.');
      if (!isLatestCartUpdate(cartUpdateSequence)) return;

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml === 'string') {
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, check the Liquid result for shipping discounts that Shopify marked applicable.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            discountCode.value = '';
            return;
          }
        }
      }

      // The full cart is authoritative even when Shopify omits requested section HTML.
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, cartUpdateSequence));
      cartUpdateDispatched = true;
      discountResponseConfirmed = true;
      if (typeof newHtml === 'string') {
        morphSection(this.dataset.sectionId, newHtml);
      }
      else throw new Error('The updated cart section was missing.');
    } catch (error) {
      if (cartUpdateSequence === undefined || !isLatestCartUpdate(cartUpdateSequence)) return;
      if (!cartUpdateDispatched) cartUpdateDispatched = await this.#reconcileCart(cartUpdateSequence);
      if (abortController.signal.aborted || !isLatestCartUpdate(cartUpdateSequence)) return;

      console.error(cartUpdateDispatched
        ? discountResponseConfirmed
          ? 'The discount updated the cart, but its section was unavailable; refreshing the section.'
          : 'The discount result is uncertain; the current cart was refreshed, and its section is being refreshed.'
        : 'The discount result could not be confirmed; refreshing the cart section.', error);
      try {
        await sectionRenderer.renderSection(this.dataset.sectionId, {
          cache: false,
          shouldMorph: () => isLatestCartUpdate(cartUpdateSequence),
        });
      } catch (refreshError) {
        console.error('Failed to refresh the cart section after the discount update.', refreshError);
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    });
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    return runCartMutation(async (cartUpdateSequence) => {
      let cartUpdateDispatched = false;
      let discountResponseConfirmed = false;
      try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: this.#sectionsToUpdate() }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Shopify did not confirm the discount removal.');
      const data = await response.json();
      if (!isFullCartResponse(data)) throw new Error('Shopify returned an incomplete cart after discount removal.');
      if (!isLatestCartUpdate(cartUpdateSequence)) return;
      if (data.discount_codes.some((/** @type {{ code?: string }} */ discount) => discount.code === discountCode)) {
        throw new Error('Shopify still lists the discount after removal.');
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, cartUpdateSequence));
      cartUpdateDispatched = true;
      discountResponseConfirmed = true;
      if (typeof newHtml === 'string') morphSection(this.dataset.sectionId, newHtml);
      else throw new Error('The updated cart section was missing.');
    } catch (error) {
      if (cartUpdateSequence === undefined || !isLatestCartUpdate(cartUpdateSequence)) return;
      if (!cartUpdateDispatched) cartUpdateDispatched = await this.#reconcileCart(cartUpdateSequence);
      if (abortController.signal.aborted || !isLatestCartUpdate(cartUpdateSequence)) return;

      console.error(cartUpdateDispatched
        ? discountResponseConfirmed
          ? 'The cart updated after discount removal, but its section was unavailable; refreshing the section.'
          : 'The discount removal result is uncertain; the current cart was refreshed, and its section is being refreshed.'
        : 'The discount removal result could not be confirmed; refreshing the cart section.', error);
      try {
        await sectionRenderer.renderSection(this.dataset.sectionId, {
          cache: false,
          shouldMorph: () => isLatestCartUpdate(cartUpdateSequence),
        });
      } catch (refreshError) {
        console.error('Failed to refresh the cart section after removing the discount.', refreshError);
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      }
    });
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
